import './profile-page.scss';

import NiceModal from '@ebay/nice-modal-react';
import { useTonWallet } from '@tonconnect/ui-react';
import { ArrowDownCircle,Award, BarChart3, Clock, Settings, UserPlus, Users2 } from 'lucide-react';
import React, { type CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch,useAppSelector } from '@/app/store/hooks';
import {
  referralActions,
  selectReferralJoinError,
  selectReferralJoining,
  selectReferralJoinSuccess,
} from '@/entities/referral';
import { selectUser, selectWallet } from '@/entities/user';
import GiftIcon from '@/shared/assets/icons/gift.svg?react';
import StarIcon from '@/shared/assets/icons/star.svg?react';
import TonIcon from '@/shared/assets/icons/ton.svg?react';
import { MODALS } from '@/shared/config/modals';
import { twa } from '@/shared/lib/telegram/twa';
import { PlayerAvatar } from '@/shared/ui/PlayerAvatar';
function formatAddress(raw: string): string {
  if (raw.length <= 10) return raw;
  return `${raw.slice(0, 6)}...${raw.slice(-4)}`;
}

const showStats = () => NiceModal.show(MODALS.STATS);
const showAchievements = () => NiceModal.show(MODALS.ACHIEVEMENTS);
const showActivity = () => NiceModal.show(MODALS.RECENT_ACTIVITY);
const showReferrals = () => NiceModal.show(MODALS.REFERRAL);
const showSettings = () => NiceModal.show(MODALS.SETTINGS);
const showWithdraw = () => NiceModal.show(MODALS.WITHDRAW);

export const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const tonWallet = useAppSelector(selectWallet('TON'));
  const starsWallet = useAppSelector(selectWallet('STARS'));
  const wallet = useTonWallet();
  const joining = useAppSelector(selectReferralJoining);
  const joinSuccess = useAppSelector(selectReferralJoinSuccess);
  const joinError = useAppSelector(selectReferralJoinError);
  const [refCode, setRefCode] = useState('');
  const tonBalance = tonWallet?.balance ?? 0;
  const starsBalance = starsWallet?.balance ?? 0;
  const tonLockedBonus = tonWallet?.lockedBonus ?? 0;
  const starsLockedBonus = starsWallet?.lockedBonus ?? 0;
  const hasLockedBonus = tonLockedBonus > 0 || starsLockedBonus > 0;

  useEffect(() => {
    if (!user?.referrerId) {
      const start = twa.getStartParam();
      setRefCode(start?.startsWith('ref_') ? start.slice(4) : '');
    } else {
      setRefCode('');
    }
  }, [user?.referrerId]);

  const handleJoinReferral = () => {
    const code = refCode.trim();
    if (!code || joining) return;
    dispatch(referralActions.joinRequest(code));
  };

  if (!user) {
    return (
      <div className="profile-page profile-page--loading">
        <p className="profile-page__loading-text">{t('common.loadingProfile')}</p>
      </div>
    );
  }

  const actions = [
    { label: t('profile.stats'), icon: BarChart3, mod: 'cyan', onClick: showStats },
    { label: t('profile.referrals'), icon: Users2, mod: 'purple', onClick: showReferrals },
    { label: t('profile.achievements'), icon: Award, mod: 'gold', onClick: showAchievements },
    { label: t('profile.activity'), icon: Clock, mod: 'green', onClick: showActivity },
  ];

  return (
    <div className="profile-page">
      <div className="profile-page__header profile-page__header--in">
        <div className="profile-page__header-inner">
          <div className="profile-page__avatar-wrap">
            <PlayerAvatar id={user.id} avatar={user.avatar} size={72} />
            <div className="profile-page__status" aria-hidden />
          </div>

          <h2 className="profile-page__username">{user.username}</h2>
          {wallet && (
            <div className="profile-page__wallet-address">
              {formatAddress(wallet.account.address)}
            </div>
          )}

          <div className="profile-page__balances">
            <div className="profile-page__balance profile-page__balance--ton">
              <TonIcon className="profile-page__balance-icon" />
              <span className="profile-page__balance-value">{tonBalance.toFixed(2)}</span>
              <span className="profile-page__balance-currency">TON</span>
            </div>
            <div className="profile-page__balance profile-page__balance--stars">
              <StarIcon className="profile-page__balance-icon" />
              <span className="profile-page__balance-value">{starsBalance.toFixed(0)}</span>
              <span className="profile-page__balance-currency">Stars</span>
            </div>
          </div>
          <span className="profile-page__balance-label">{t('profile.inGameBalance')}</span>
          {hasLockedBonus && (
            <div className="profile-page__bonus-panel">
              <div className="profile-page__bonus-header">
                <div className="profile-page__bonus-title-wrap">
                  <GiftIcon className="profile-page__bonus-icon" />
                  <span className="profile-page__bonus-title">{t('profile.welcomeBonusLocked')}</span>
                </div>
                <span className="profile-page__bonus-note">{t('profile.unlockBonusHint')}</span>
              </div>
              <div className="profile-page__bonus-grid">
                <div className="profile-page__bonus-card">
                  <span className="profile-page__bonus-card-label">{t('profile.tonBonus')}</span>
                  <span className="profile-page__bonus-card-value">{tonLockedBonus.toFixed(2)}</span>
                </div>
                <div className="profile-page__bonus-card profile-page__bonus-card--stars">
                  <span className="profile-page__bonus-card-label">{t('profile.starsBonus')}</span>
                  <span className="profile-page__bonus-card-value">{starsLockedBonus.toFixed(0)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="profile-page__header-actions">
            <button
              type="button"
              className="profile-page__icon-btn profile-page__icon-btn--withdraw profile-page__icon-btn--tappable"
              onClick={showWithdraw}>
              <ArrowDownCircle size={18} />
              <span>{t('profile.withdraw')}</span>
            </button>
            <button
              type="button"
              className="profile-page__icon-btn profile-page__icon-btn--settings profile-page__icon-btn--tappable"
              onClick={showSettings}>
              <Settings size={18} />
              <span>{t('profile.settings')}</span>
            </button>
          </div>
        </div>
        <div className="profile-page__header-glow" />
      </div>

      {!user.referrerId && (
        <div
          className="profile-page__section profile-page__section--in"
          style={{ '--section-in-delay': '0.25s' } as CSSProperties}>
          <div className="profile-page__no-ref-block">
            <div className="profile-page__no-ref-header">
              <div className="profile-page__no-ref-icon">
                <UserPlus size={18} />
              </div>
              <span className="profile-page__no-ref-title">{t('profile.noReferrer')}</span>
            </div>
            <p className="profile-page__no-ref-desc">{t('profile.noReferrerDesc')}</p>
            {joinSuccess ? (
              <div className="profile-page__no-ref-success">{joinSuccess}</div>
            ) : (
              <>
                <div className="profile-page__no-ref-join-row">
                  <input
                    className="profile-page__no-ref-input"
                    type="text"
                    placeholder={t('profile.referralCodePlaceholder')}
                    value={refCode}
                    onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                    maxLength={16}
                    disabled={joining}
                  />
                  <button
                    type="button"
                    className="profile-page__no-ref-btn"
                    onClick={handleJoinReferral}
                    disabled={!refCode.trim() || joining}>
                    {t('profile.join')}
                  </button>
                </div>
                {joinError && <div className="profile-page__no-ref-error">{joinError}</div>}
              </>
            )}
          </div>
        </div>
      )}

      <div className="profile-page__actions">
        {actions.map((action, index) => {
          const Icon = action.icon;
          return (
            <button
              key={action.label}
              type="button"
              className={`profile-page__action-btn profile-page__action-btn--${action.mod} profile-page__action-btn--in profile-page__action-btn--tappable`}
              onClick={action.onClick}
              style={{ '--action-in-delay': `${0.25 + index * 0.05}s` } as CSSProperties}>
              <div className="profile-page__action-icon">
                <Icon size={20} />
              </div>
              <span className="profile-page__action-label">{action.label}</span>
              <span className="profile-page__action-arrow">›</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
